import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import {
  closeMainModal,
  openMainModal,
  getAllWorkBagUsers,
  sendIdentityPhotos,
} from "../../store/actions";
import { useNavigate } from "react-router-dom";
import PhotoUploadModal from "../../components/FileUpload/PhotosUploadModal";
import { X, Briefcase, MapPin, DollarSign, ExternalLink, Clock } from "lucide-react";
import ApplyExternalJobOffer from "./ApplyExternalJobOffer.tsx";
import ModalOverlay from "../../components/UI/ModalOverlay/ModalOverlay.tsx";
import { useActionGuard } from "../../hooks/useActionGuard.ts";
import { fireAnalytics } from "../../utils/firebase";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import { formatSalary, formatDate } from "../../components/MercadoDeTrabajo/WorkOfferDetailsModal.tsx";
import styles from "./WorkApplyModal.module.scss";

interface WorkOfferUser {
  username?: string;
  type?: string;
  razon_social?: string;
  name?: string;
  last_name?: string;
}

interface WorkOfferSkill {
  skill?: { name: string };
}

interface WorkOfferZone {
  jurisdiccion?: { name: string };
  city?: string;
}

export interface WorkOffer {
  id?: string | number;
  user?: WorkOfferUser & { email?: string };
  skill?: WorkOfferSkill[];
  workZones?: WorkOfferZone[];
  salary?: string;
  salaryType?: string;
  activeUntil?: string;
  alternativeContact?: string;
  description?: string;
}

interface WorkApplyModalProps {
  workBagUsers: Array<{ email?: string; identity_validated?: boolean }>;
  setShowApplyModal: (show: boolean) => void;
  openMainModal: (config: any) => void;
  closeMainModal: () => void;
  getAllWorkBagUsers: () => void;
  sendIdentityPhotos: (files: { dniFront: File; dniBack: File; selfie: File }, email: string, navigate: (path: string) => void) => void;
  workOffer?: WorkOffer | null;
  forbiddenClose?: boolean;
  setShowForm?: () => void;
  skipWorkBagUserCheck?: boolean;
  prefilledEmail?: string;
  identityValidated?: boolean;
  userData?: { id?: string | number };
}

const formatSalaryDisplay = (salary?: string, type?: string) => {
  if (!salary || salary === "0.00" || Number.parseFloat(salary) === 0) return "A convenir";
  return formatSalary(salary, type);
};

const WorkApplyModal: React.FC<WorkApplyModalProps> = ({
  workBagUsers,
  setShowApplyModal,
  openMainModal,
  closeMainModal,
  getAllWorkBagUsers,
  workOffer,
  sendIdentityPhotos,
  forbiddenClose = false,
  setShowForm,
  skipWorkBagUserCheck = false,
  prefilledEmail,
  identityValidated: identityValidatedProp,
  userData,
}) => {
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [showApplyExternalJobOffer, setShowApplyExternalJobOffer] = useState(false);
  const navigate = useNavigate();
  const { guard } = useActionGuard();

  useEffect(() => {
    if (!skipWorkBagUserCheck) {
      getAllWorkBagUsers();
    }
  }, [getAllWorkBagUsers, skipWorkBagUserCheck]);

  const isExternalOffer =
    !workOffer || (workOffer?.user?.username?.toLowerCase() ?? "") === "gonzalo@edify.la";

  const companyLabel = workOffer?.user
    ? workOffer.user.type === "COMPANY"
      ? workOffer.user.razon_social ?? "Empresa"
      : [workOffer.user.name, workOffer.user.last_name].filter(Boolean).join(" ").trim() || "Publicador"
    : "Oferta";

  const primaryZone =
    workOffer?.workZones?.[0]?.jurisdiccion?.name ?? workOffer?.workZones?.[0]?.city ?? null;
  const skillNames = (workOffer?.skill ?? []).map((s) => s?.skill?.name).filter(Boolean) as string[];
  const salaryLabel = formatSalaryDisplay(workOffer?.salary, workOffer?.salaryType);
  const activeUntilLabel = workOffer?.activeUntil ? formatDate(workOffer.activeUntil) : null;
  const hasOfferContext = Boolean(
    workOffer &&
      ((!isExternalOffer && companyLabel) ||
        skillNames.length > 0 ||
        primaryZone ||
        salaryLabel !== "A convenir" ||
        activeUntilLabel)
  );

  const handlePhotoModalState = () => setShowPhotoUploadModal(true);

  const handleSendPhotos = (files: { dniFront: File; dniBack: File; selfie: File }) => {
    const email = formik.values.email;
    sendIdentityPhotos(files, email, navigate);
  };

  const handleSubmit = guard(
    "apply_to_offer",
    (values: { email: string }) => {
      const emailToUse = values.email.trim().toLowerCase();
      const isUserInWorkBag = skipWorkBagUserCheck
        ? null
        : workBagUsers.find((user) => (user.email ?? "").toLowerCase() === emailToUse);
      const identityValidated = skipWorkBagUserCheck
        ? Boolean(identityValidatedProp)
        : Boolean(isUserInWorkBag?.identity_validated);

      if (!skipWorkBagUserCheck && !isUserInWorkBag) {
        setShowApplyModal(false);
        openMainModal({
          title: "¡Ups!",
          body: "No encontramos tu email en nuestra base de datos. Para postularte a esta oferta, debes inscribirte en la bolsa de trabajo.",
          isSuccess: true,
          iconType: "warning",
          noLabel: "Inscribirme",
          customCloseMainModal: () => {
            navigate("/bolsadetrabajo");
            closeMainModal();
            if (typeof setShowForm === "function") setShowForm();
          },
        });
      } else if (!identityValidated) {
        openMainModal({
          title: "Validación de identidad requerida",
          iconType: "scanFace",
          body: isExternalOffer
            ? "Para acceder al contacto de esta oferta y postularte, debes validar tu identidad. Subí fotos de tu DNI y te notificaremos por email cuando esté aprobado; entonces podrás ver el contacto."
            : "Para postularte, debes validar tu identidad. Es muy fácil, solo necesitas subir unas fotos de tu DNI.",
          yesLabel: "Subir fotos",
          confirmHandler: () => {
            closeMainModal();
            handlePhotoModalState();
          },
        });
      } else if (isExternalOffer) {
        setShowApplyExternalJobOffer(true);
        closeMainModal();
      } else {
        setShowApplyModal(false);
        closeMainModal();
        const targetPath = skipWorkBagUserCheck
          ? `/bolsa-trabajo/${workOffer?.id}`
          : `/bolsadetrabajo/${workOffer?.id}`;
        navigate(targetPath);
      }
    },
    () => {
      fireAnalytics("apply_to_offer", {
        edify_user_id: userData?.id || "",
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: "work_bag",
        action: "apply_to_offer",
        offer_id: workOffer?.id || "",
      });
    }
  );

  const formik = useFormik({
    initialValues: { email: (prefilledEmail ?? "").toLowerCase() },
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: Yup.object({
      email: Yup.string().email("Email inválido").required("Campo requerido"),
    }),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleClosePhotoUploadModal = () => setShowPhotoUploadModal(false);
  const handleClose = () => setShowApplyModal(false);

  if (showApplyExternalJobOffer) {
    return (
      <ApplyExternalJobOffer
        workOffer={workOffer}
        isOpen={showApplyExternalJobOffer}
        onClose={() => {
          setShowApplyExternalJobOffer(false);
          setShowApplyModal(false);
        }}
      />
    );
  }

  return (
    <>
      <ModalOverlay isOpen={true} onClose={handleClose}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <header className={styles.header}>
            <h2 className={styles.title}>Postularte a esta oferta</h2>
            {isExternalOffer && !forbiddenClose && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            )}
          </header>

          <div className={styles.body}>
            {isExternalOffer && (
              <div className={styles.externalBadge}>
                <ExternalLink size={16} />
                <span>Búsqueda externa: te acercamos el contacto, la contratación la gestiona quien publicó.</span>
              </div>
            )}
            {hasOfferContext && (
              <div className={styles.offerContext}>
                <p className={styles.offerContextLabel}>
                  {isExternalOffer ? "Detalles de la búsqueda" : "Estás por postularte a:"}
                </p>
                <div className={styles.offerContextCard}>
                  {!isExternalOffer && companyLabel && (
                    <div className={styles.offerContextRow}>
                      <Briefcase size={16} className={styles.offerContextIcon} />
                      <span className={styles.offerContextValue}>{companyLabel}</span>
                    </div>
                  )}
                  {skillNames.length > 0 && (
                    <div className={styles.offerContextRow}>
                      <span className={styles.offerContextChips}>
                        {skillNames.slice(0, 3).join(" · ")}
                      </span>
                    </div>
                  )}
                  {(primaryZone || salaryLabel !== "A convenir" || activeUntilLabel) && (
                    <div className={styles.offerContextMeta}>
                      {primaryZone && (
                        <span className={styles.offerContextMetaItem}>
                          <MapPin size={14} />
                          {primaryZone}
                        </span>
                      )}
                      {salaryLabel !== "A convenir" && (
                        <span className={styles.offerContextMetaItem}>
                          <DollarSign size={14} />
                          {salaryLabel}
                        </span>
                      )}
                      {activeUntilLabel && (
                        <span className={styles.offerContextMetaItem}>
                          <Clock size={14} />
                          Postulá hasta {activeUntilLabel}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className={styles.subtitle}>
              {prefilledEmail
                ? "Usaremos el correo de tu cuenta para completar la postulación."
                : "Ingresá tu correo electrónico para verificar tu inscripción en la bolsa de trabajo."}
            </p>

            <form
              className={`${styles.form} ${prefilledEmail ? styles.formPrefilled : ""}`}
              onSubmit={formik.handleSubmit}
            >
              {skipWorkBagUserCheck && prefilledEmail ? (
                <div className={styles.prefilledEmail}>
                  <span className={styles.prefilledEmailLabel}>Correo asociado a tu cuenta</span>
                  <span className={styles.prefilledEmailValue}>{prefilledEmail}</span>
                </div>
              ) : (
                <input
                  className={styles.input}
                  type="email"
                  placeholder="ejemplo@mail.com"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                />
              )}
              <button type="submit" className={styles.primaryButton} disabled={!formik.isValid}>
                {prefilledEmail ? "Continuar" : "Verificar y continuar"}
              </button>
            </form>
          </div>
        </div>
      </ModalOverlay>

      {showPhotoUploadModal && (
        <PhotoUploadModal
          closeModal={handleClosePhotoUploadModal}
          handleSendPhotos={handleSendPhotos}
          onClick={() => {
            setShowPhotoUploadModal(false);
            openMainModal({
              title: "¡Excelente!",
              body: "Tus fotos han sido enviadas para verificación. Te notificaremos cuando tu identidad sea aprobada.",
              isSuccess: true,
              iconType: "success",
              customCloseMainModal: () => closeMainModal(),
            });
          }}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: { register: { workBagUsers: WorkApplyModalProps["workBagUsers"] } }) => ({
  workBagUsers: state.register.workBagUsers,
});

export default connect(mapStateToProps, {
  openMainModal,
  closeMainModal,
  getAllWorkBagUsers,
  sendIdentityPhotos,
})(WorkApplyModal);
